import {
  RouteProp,
  StackActions,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useRef, useState } from 'react';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { QualificationParamList } from '../../../web-navigation/QualificationNavigator';
import { useGetStudyIntake } from '../../../hooks/study/useGetStudyIntake';
import AlertBox from '../../../components/AlertBox';
import { StudyParticipant } from '../../../models/StudyParticipant';

type QualificationProps<T> = FormElementProps<T, QualificationConfig>;

export const Qualification = <T extends object>({
  element,
  stateNode
}: QualificationProps<T>) => {
  const { emitEvent, runService } = useDynamicFormContext<any, any>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route =
    useRoute<RouteProp<QualificationParamList, 'QualificationForm'>>();
  const studyParticipantId = route.params?.studyParticipantId;
  const { qualified, redirectPath, redirectFormTemplateId, html } =
    element.config || {};

  const { loadingStudyIntakeError, getStudyIntake } = useGetStudyIntake();

  const [qualifiedStudyParticipant, setQualifiedStudyParticipant] =
    useState<StudyParticipant>();
  const [qualifyError, setQualifyError] = useState<string | undefined>();

  // Capture stateNode.type at the moment we kick off the qualification.
  // During the network round-trip the state machine can transition away from
  // 'final'; without this capture a post-await stateNode?.type check would
  // return false and 'final' would silently never emit — leaving the form
  // stuck.
  const wasFinalAtFireRef = useRef<boolean>(false);

  // The AbortController doubles as the single-fire guard: it is only ever
  // created once (`if (abortRef.current) return`), so qualification POSTs
  // exactly once even across remounts / dep changes. It is aborted on unmount
  // to cancel the in-flight service via its AbortSignal — the cancellation the
  // old useApiPost path could not do.
  const abortRef = useRef<AbortController | undefined>(undefined);

  // Abort the in-flight qualify on unmount only — leaving the screen cancels
  // the service via its AbortSignal.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Qualify the participant through the state-machine invoke service. The
  // service owns the API call and AbortSignal cancellation (CD-207), replacing
  // the previous useQualifyStudyParticipant hook (which had no fire-once guard
  // and could not abort).
  useEffect(() => {
    if (!studyParticipantId) return;
    if (abortRef.current) return; // already qualifying / qualified

    const controller = new AbortController();
    abortRef.current = controller;
    setQualifyError(undefined);
    // Read stateNode?.type at the moment qualification is kicked off and stash
    // it on the ref so the post-success handler can use it after the network
    // round-trip.
    wasFinalAtFireRef.current = stateNode?.type === 'final';

    (async () => {
      try {
        const result = (await runService('qualification', {
          data: { studyParticipantId, isQualified: qualified == 'Yes' },
          signal: controller.signal
        })) as StudyParticipant;
        if (controller.signal.aborted) return;
        setQualifiedStudyParticipant(result);
      } catch (err: any) {
        if (controller.signal.aborted) return;
        // allow a retry on a later dep change — and surface the failure
        // instead of leaving the spinner up indefinitely
        abortRef.current = undefined;
        setQualifyError(
          err?.message || 'Unable to complete qualification. Please try again.'
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyParticipantId, qualified, runService, redirectPath]);

  useEffect(() => {
    if (qualifiedStudyParticipant) {
      const { study } = qualifiedStudyParticipant;
      const handleQualification = async () => {
        if (wasFinalAtFireRef.current) {
          await emitEvent('final', { transitions: [] });
        }
        if (
          route.params.fromJoinScreen &&
          qualifiedStudyParticipant.qualified &&
          study?.qualificationFormTemplateId
        ) {
          if (study.isPublic) {
            const studyIntake = await getStudyIntake(
              qualifiedStudyParticipant.id
            );
            if (!studyIntake) return;
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'StudiesNav',
                  state: {
                    index: 1,
                    routes: [
                      { name: 'MyStudies' },
                      {
                        name: 'FormTemplate',
                        params: {
                          formTemplateId: studyIntake.formTemplateId,
                          entityDataId: studyIntake.id,
                          studyParticipantId: qualifiedStudyParticipant.id,
                          message: html
                        }
                      }
                    ]
                  }
                }
              ]
            });
          } else if (!study.isPublic) {
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'StudiesNav',
                  state: {
                    index: 1,
                    routes: [
                      { name: 'MyStudies' },
                      {
                        name: 'QualificationComplete',
                        params: {
                          displayNotQualified: false,
                          message: html,
                          fromJoinScreen: route.params.fromJoinScreen
                        }
                      }
                    ]
                  }
                }
              ]
            });
          }
        } else if (
          route.params.fromJoinScreen &&
          !qualifiedStudyParticipant.qualified
        ) {
          navigation.reset({
            index: 0,
            routes: [
              {
                name: 'StudiesNav',
                state: {
                  index: 1,
                  routes: [
                    { name: 'MyStudies' },
                    {
                      name: 'QualificationComplete',
                      params: {
                        displayNotQualified: true,
                        message: html,
                        fromJoinScreen: route.params.fromJoinScreen
                      }
                    }
                  ]
                }
              }
            ]
          });
        } else {
          navigation.dispatch(
            StackActions.replace('Qualification', {
              screen: 'QualificationComplete',
              params: {
                studyParticipantId,
                message: html
              }
            })
          );
        }
        return;
      };
      // wait till next render loop since StudyEventFormDataSource doesn't
      // register the 'final' subscribe immediately
      setTimeout(() => handleQualification());
    }
  }, [
    // stateNode intentionally not in deps — this effect reads
    // wasFinalAtFireRef.current (set in the first effect) rather than
    // stateNode?.type, so re-running on state-machine transitions
    // would be wasted work.
    navigation,
    redirectPath,
    redirectFormTemplateId,
    emitEvent,
    studyParticipantId,
    route.params.fromJoinScreen,
    qualified,
    getStudyIntake,
    route,
    qualifiedStudyParticipant
  ]);

  return loadingStudyIntakeError ? (
    <AlertBox severity='error'>{loadingStudyIntakeError.message}</AlertBox>
  ) : qualifyError ? (
    <AlertBox severity='error'>{qualifyError}</AlertBox>
  ) : (
    <ActivityIndicatorScreen backgroundColor='transparent' />
  );
};

interface QualificationConfig {
  qualified?: string;
  redirectPath?: string;
  redirectFormTemplateId?: number;
  html?: string;
}

export const QualificationPlugin: FormElementPlugin<any, QualificationConfig> =
  {
    name: 'Qualification',
    type: 'Qualification',
    effectOnMount: true,
    element: Qualification as React.FC<
      FormElementProps<any, QualificationConfig>
    >
  };
